import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaPlus, FaEdit, FaTrash, FaTimes } from "react-icons/fa";
import "./Plans.css";

const Subscription = () => {
  const [plans, setPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [newFeature, setNewFeature] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE = "https://back.varankal.com";

  useEffect(() => {
    document.body.classList.add("body-background");
    fetchPlans();
    return () => {
      document.body.classList.remove("body-background");
    };
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/plans`);
      setPlans(res.data);
    } catch (err) {
      setError("Failed to load plans.");
      console.error("Error fetching plans:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan({ ...plan });
  };

  const handleSave = async () => {
    try {
      const res = await axios.put(
        `${API_BASE}/api/plans/${editingPlan._id}`,
        editingPlan
      );

      setPlans(plans.map((p) => (p._id === editingPlan._id ? res.data : p)));
      setEditingPlan(null);

      alert("Plan updated successfully!");
    } catch (err) {
      console.error("Error updating plan:", err);
      alert("Failed to update plan. Please try again.");
    }
  };


  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this plan permanently?"
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE}/api/plans/${id}`);
      setPlans(plans.filter((plan) => plan._id !== id));
    } catch (err) {
      console.error("Error deleting plan:", err);
    }
  };

  const handleFeatureAdd = () => {
    if (newFeature.trim()) {
      setEditingPlan({
        ...editingPlan,
        features: [...(editingPlan.features || []), newFeature],
      });
      setNewFeature("");
    }
  };

  const handleFeatureEdit = (index, value) => {
    const updatedFeatures = [...(editingPlan.features || [])];
    updatedFeatures[index] = value;
    setEditingPlan({ ...editingPlan, features: updatedFeatures });
  };

  const handleFeatureDelete = (index) => {
    const updatedFeatures = (editingPlan.features || []).filter((_, i) => i !== index);
    setEditingPlan({ ...editingPlan, features: updatedFeatures });
  };

  return (
    <div className="subscription-page">
      {loading ? (
        <p>Loading plans...</p>
      ) : error ? (
        <p className="error-message">{error}</p>
      ) : (
        <div className="plans">
          {plans.map((plan) => (
            <div key={plan._id} className="plan">
              <h2>
                {plan.name}{" "}
                <span>
                  {plan.durationInMonths} month{plan.durationInMonths > 1 ? "s" : ""}
                </span>
              </h2>
              <p className="discount">
                {plan.discount} <span>{plan.originalPrice}</span>
              </p>
              <p className="price">₹{plan.price}</p>

              <ul>
                {plan.features?.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
              <div className="actions">
                <button className="edit-btn" onClick={() => handleEdit(plan)}>
                  <FaEdit /> Edit
                </button>
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(plan._id)}
                >
                  <FaTrash /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editingPlan && (
        <>
          <div
            className="edit-panel-overlay"
            onClick={() => setEditingPlan(null)}
          ></div>
          <div className="edit-panel">
            <a className="close-button" onClick={() => setEditingPlan(null)}>
              <FaTimes />
            </a>
            <h2>Edit Plan</h2>

            <div className="form-grid">
              {["name", "price", "discount", "originalPrice"].map((field) => (
                <div className="input-container" key={field}>
                  <label htmlFor={field} className="input-label">
                    {field.charAt(0).toUpperCase() + field.slice(1)}
                  </label>
                  <div className="input-with-icon">
                    <input
                      id={field}
                      type={field === "price" ? "number" : "text"}
                      value={editingPlan[field] || ""}
                      onChange={(e) =>
                        setEditingPlan({
                          ...editingPlan,
                          [field]:
                            field === "price"
                              ? Number(e.target.value)
                              : e.target.value,
                        })
                      }
                      className="editable-input"
                      placeholder={`Enter ${field}`}
                    />
                    <FaEdit className="edit-icon" />
                  </div>
                </div>
              ))}

              {/* Duration in months input */}
              <div className="input-container" key="durationInMonths">
                <label htmlFor="durationInMonths" className="input-label">
                  Duration (Months)
                </label>
                <div className="input-with-icon">
                  <input
                    id="durationInMonths"
                    type="number"
                    min="1"
                    value={editingPlan.durationInMonths || ""}
                    onChange={(e) => {
                      const months = e.target.value;
                      setEditingPlan({
                        ...editingPlan,
                        durationInMonths: months ? Number(months) : 0,
                      });
                    }}
                    className="editable-input"
                    placeholder="Enter duration in months"
                  />
                  <FaEdit className="edit-icon" />
                </div>
              </div>
            </div>

            <h3>Features:</h3>
            <ul className="feature-list">
              {editingPlan.features?.map((feature, idx) => (
                <li key={idx} className="feature-item">
                  <div className="input-container">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => handleFeatureEdit(idx, e.target.value)}
                      className="feature-input"
                    />
                    <FaTrash
                      className="delete-icon"
                      onClick={() => handleFeatureDelete(idx)}
                    />
                  </div>
                </li>
              ))}
            </ul>

            <div className="add-feature-container">
              <input
                type="text"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                placeholder="Add new feature"
              />
              <button className="add-feature" onClick={handleFeatureAdd}>
                <FaPlus /> Add
              </button>
            </div>

            <button className="save-button" onClick={handleSave}>
              Save Changes
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Subscription;
